import classNames from 'classnames'
import * as React from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { type CommonError } from 'src/api/base'
import Statements from 'src/assets/icons/customIcons/page-icons/Statements'
import { errorMessage } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { sanitizeHtml } from 'src/helpers/sanitizeHtml'
import { STATEMENT_STATUS_ROW_CLASSES } from 'src/helpers/statementStatus'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import StatementsFilter from './StatementsFilter'
import { formatStatementDate, PER_PAGE_OPTIONS, searchParamsToQuery, type StatementLinkState } from './statementsQuery'

export default function AdministratorStatementsPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('statement:statements')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()

  const breadcrumbsItems = [
    {
      page: `${t('system:administration_sidebar')} - ${t('statement:statements')}`,
      path: `/${locale}/administrator/administration/statements`,
    },
  ]

  return (
    <Section title={t('statement:statements')} breadcrubms={breadcrumbsItems} icon={<Statements />}>
      <StatementsFilter />
      <StatementsResults />
    </Section>
  )
}

function StatementsResults(): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [searchParams] = useSearchParams()

  const { data, error, isLoading, isValidating } = useApi({
    endpoint: Api.getAdministratorStatements,
    params: React.useMemo(() => ({ headers, query: searchParamsToQuery(searchParams) }), [headers, searchParams]),
    suspense: false,
    keepPreviousData: true,
  })

  if (isLoading && data == null) {
    return <Loader className="m-auto my-10 flex" />
  }

  if (error != null && !isValidating) {
    return (
      <NoContent
        header={t('error:an_error_occurred')}
        image={Warning}
        subHeader={errorMessage(error as unknown as CommonError)}
      />
    )
  }

  if (data == null || data.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  const options = { perPageOptions: PER_PAGE_OPTIONS }

  return (
    <div className="relative mb-7">
      {/* Searching in statement texts can take several seconds */}
      {isValidating && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
          <Loader />
        </div>
      )}

      <div className="mt-3 text-primaryTextColor" data-testid="statements-total">
        {t('statement:records_found')}: {data.meta.pagination.total.toLocaleString()}
      </div>
      <PaginationTop pagination={data.meta.pagination} options={options} />

      <Table.Table className={classNames('mt-4', isValidating && 'opacity-60')}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('common:number')}</Table.Th>
            <Table.Th>{t('statement:statement_title')}</Table.Th>
            <Table.Th>{t('student:student')}</Table.Th>
            <Table.Th>{t('common:time')}</Table.Th>
            <Table.Th>{t('common:status')}</Table.Th>
            <Table.Th>{t('discussion:comment')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.data.map((statement) => (
            <StatementRow key={`${statement.id}-${statement.semId}`} statement={statement} />
          ))}
        </Table.Tbody>
      </Table.Table>

      <PaginationBottom pagination={data.meta.pagination} />
    </div>
  )
}

function StatementRow({ statement }: { readonly statement: Api.AdministratorStatement }): JSX.Element {
  const locale = useLocale()
  const { search } = useLocation()
  const comment = React.useMemo(() => sanitizeHtml(statement.comment), [statement.comment])
  const linkState: StatementLinkState = { listSearch: search }

  return (
    <Table.Tr className={STATEMENT_STATUS_ROW_CLASSES[statement.status]}>
      <Table.Td className="whitespace-nowrap">
        <Link
          to={`/${locale}/administrator/administration/statements/${statement.id}/${statement.semId}`}
          state={linkState}
        >
          {statement.number}
        </Link>
      </Table.Td>
      <Table.Td>{statement.title}</Table.Td>
      <Table.Td>{`${statement.student.fullName} (${statement.student.persNum})`}</Table.Td>
      <Table.Td className="whitespace-nowrap">{formatStatementDate(statement.createdAt)}</Table.Td>
      <Table.Td>{statement.statusText ?? '—'}</Table.Td>
      <Table.Td>
        <div className="max-w-md break-words" dangerouslySetInnerHTML={{ __html: comment }} />
      </Table.Td>
    </Table.Tr>
  )
}
